import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Globe2, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { ICON_SIZE } from '@rynora/theme';
import { LegalLayout } from '../components/LegalLayout';
import { REGIONS, type RegionDescriptor, type RegionId, type RegionsCatalogueResponse } from '@rynora/contracts';
import { getApiBaseUrl } from '../../../lib/config';
import '../legal.css';

/**
 * /trust/regions — public, honest accounting of Rynora's region
 * footprint (Batch 3 PR #2).
 *
 * Two responsibilities:
 *
 *   1. Render the catalogue from `@rynora/contracts` so the page works
 *      offline / on storybook / on broken-backend deployments.
 *   2. Optimistically layer the live `GET /api/regions` response on
 *      top, so we show "currently serving from us-phx" with the
 *      actual cluster identity, plus the operator's roster of
 *      `availableRegions` for the GA badge.
 *
 * The page is intentionally text-heavy. Buyers' procurement teams
 * read this page line-by-line; the goal is to leave no question
 * unanswered about which region runs what.
 */
export function RegionsPage() {
  const [live, setLive] = useState<RegionsCatalogueResponse | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const url = `${getApiBaseUrl()}/api/regions`;
    fetch(url, { credentials: 'omit' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as RegionsCatalogueResponse;
      })
      .then((body) => {
        if (!cancelled) setLive(body);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLiveError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const availableRegions: RegionId[] = live?.availableRegions ?? (['us-phx'] as RegionId[]);
  const currentRegion: RegionId = live?.currentRegion ?? 'us-phx';

  return (
    <LegalLayout
      title="Regions & data residency"
      updated="June 1, 2026"
      summary="Which Rynora SaaS regions are live today, which are in design-partner alpha, and which are on the roadmap. The line between marketing and reality is drawn explicitly."
      needsReview={false}
    >
      <div className="legal-banner" role="note">
        <Globe2 size={ICON_SIZE.md} aria-hidden="true" />
        <div>
          <strong>Plain-English summary.</strong> Today every Rynora SaaS organisation runs in our single{' '}
          <code>us-phx</code> region (Oracle Cloud Infrastructure, us-phoenix-1). EU-FRA is in design-partner alpha
          (cluster not yet provisioned; first paid customer gates the build). AP-SYD is publicly committed but has no
          concrete delivery date. EU and UK orgs are covered by EU SCCs + UK IDTA in the meantime, both documented in
          our DPA.
        </div>
      </div>

      <h2>The region catalogue</h2>
      <p>
        The catalogue below is the same one returned by <code>GET /api/regions</code> and is the single source of truth
        for the backend, the frontend, the trust pages, and Rynora's DPA. Adding a new region requires a contract
        migration; removing one is a breaking change we do not do.
      </p>
      {liveError && (
        <p className="legal-meta" role="note">
          Couldn't reach the live region API (<code>{liveError}</code>) — the table below shows the static catalogue
          shipped with the current Rynora release. The live status will resolve once the cluster is reachable from your
          browser.
        </p>
      )}
      <table>
        <thead>
          <tr>
            <th>Region</th>
            <th>Jurisdiction</th>
            <th>Cloud datacenter</th>
            <th>Status</th>
            <th>Hostname</th>
            <th>GA date</th>
          </tr>
        </thead>
        <tbody>
          {REGIONS.map((r: RegionDescriptor) => {
            const isCurrent = r.id === currentRegion;
            const isAvailable = availableRegions.includes(r.id);
            return (
              <tr key={r.id}>
                <td>
                  <strong>{r.displayName}</strong> <code className="legal-region-id">{r.id}</code>
                  {isCurrent && <div className="legal-region-note">You are reading this page from this region.</div>}
                </td>
                <td>{r.jurisdiction}</td>
                <td>
                  <code>{r.cloudRegion}</code>
                </td>
                <td>
                  <RegionStatusBadge status={r.status} availableInCluster={isAvailable} />
                </td>
                <td>
                  {r.hostname ? (
                    <a href={`https://${r.hostname}`}>{r.hostname}</a>
                  ) : (
                    <span className="legal-meta">—</span>
                  )}
                </td>
                <td>{r.gaDate || <span className="legal-meta">TBD</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h2>What the statuses mean</h2>
      <ul>
        <li>
          <strong>GA</strong> — the region is live, generally available to any org, and on the standard SLA. Any new
          signup can pick it.
        </li>
        <li>
          <strong>Design-partner alpha</strong> — Rynora has committed to standing the region up, but the cluster build
          is gated on a first paid enterprise design partner. Enrol by emailing{' '}
          <a href="mailto:enterprise@rynora.app">enterprise@rynora.app</a>. The region is not on the standard SLA until
          it ships GA.
        </li>
        <li>
          <strong>Planned</strong> — the region is on the public roadmap. There is no concrete delivery date and no
          committed buyer yet. Removing a region from the catalogue is a breaking change we do not do, but a region in
          this state may stay in <em>Planned</em> indefinitely.
        </li>
      </ul>

      <h2>What runs in each region today</h2>
      <p>
        Every live region runs the full Rynora control plane in a single Oracle Cloud tenancy: API, Postgres, Playwright
        runners, Caddy, Cloudflare-fronted DNS. The single-cluster topology is deliberate; multi-region failover is on
        the roadmap (see <Link to="/trust/sla">/trust/sla</Link>) but not in scope for the v0 region framework.
      </p>
      <ul>
        <li>
          <strong>US-PHX (live):</strong> Oracle Cloud us-phoenix-1 + Backblaze B2 US-West for artefact storage + AWS S3
          eu-north-1 (Stockholm) for encrypted backups + Cloudflare US PoPs + Sentry SaaS (US) + Grafana Cloud + Better
          Stack (EU). Sub-processor full list in <Link to="/trust/subprocessors">/trust/subprocessors</Link>.
        </li>
        <li>
          <strong>EU-FRA (design-partner alpha):</strong> when the cluster ships, it will run a parallel sub-processor
          set with Sentry's EU instance, Grafana's EU region, Resend's EU pool, and an EU Backblaze bucket — so that
          customer data, error telemetry, metrics, and outbound email all stay inside the EU. The DPA addendum that
          documents this split is in draft; design-partner orgs see it first.
        </li>
        <li>
          <strong>AP-SYD (planned):</strong> no concrete sub-processor split yet. The expectation is Sentry US + Grafana
          AP region + Backblaze AP-Tokyo + Cloudflare APAC PoPs, but the matrix is locked when a design partner commits.
        </li>
      </ul>

      <h2>Cross-region migration</h2>
      <p>
        v0 ships the <em>request-recording</em> half of cross-region migration: an org owner can{' '}
        <code>POST /api/orgs/:orgId/region</code> with a destination + reason and the intent is durably recorded in our{' '}
        <code>org_region_history</code> table. Today that endpoint always returns{' '}
        <code>412 ORG_REGION_MIGRATION_UNAVAILABLE</code> — the worker that actually copies data, flips{' '}
        <code>organizations.home_region</code>, and revokes the old cluster's grants is a follow-up engineering PR. Each
        recorded intent gets a UUID; quote it to <a href="mailto:enterprise@rynora.app">enterprise@rynora.app</a> if you
        need expedited triage.
      </p>
      <p>
        Once the worker is live, an in-flight migration moves through the states{' '}
        <code>requested → in_progress → completed</code> (or <code>failed</code> / <code>cancelled</code>); each
        transition writes a row to <code>audit_log</code> with action <code>ORG_REGION_CHANGED</code> (or
        <code>_FAILED</code> / <code>_CANCELLED</code>) so the SIEM feed picks it up immediately. The runbook for
        operators is in our handbook at <code>docs/handbook/runbooks/org-region-management.md</code>.
      </p>

      <h2>How we change this page</h2>
      <ul>
        <li>
          <strong>Adding a region</strong> — a new row appears here, with status <em>Planned</em> or{' '}
          <em>Design-partner alpha</em>. Material changes are announced 30 days in advance via the same{' '}
          <code>SUBSCRIBE</code> email-list described on <Link to="/trust/subprocessors">/trust/subprocessors</Link>.
        </li>
        <li>
          <strong>Promoting a region to GA</strong> — the row's status flips to GA, the GA date is filled in, and the
          row appears in the org-creation region picker.
        </li>
        <li>
          <strong>Removing a region</strong> — we don't. Removing a region we have publicly committed to is a breaking
          change for procurement plans; the row stays in <em>Planned</em> forever rather than disappearing.
        </li>
      </ul>

      <h2>Buyer questions</h2>
      <p>
        For a regional procurement / residency question, email <a href="mailto:trust@rynora.app">trust@rynora.app</a>.
        For the EU-FRA design-partner programme, email <a href="mailto:enterprise@rynora.app">enterprise@rynora.app</a>.
      </p>
    </LegalLayout>
  );
}

function RegionStatusBadge({
  status,
  availableInCluster,
}: {
  status: RegionDescriptor['status'];
  availableInCluster: boolean;
}) {
  if (status === 'ga' && availableInCluster) {
    return (
      <span className="legal-region-badge">
        <CheckCircle2 size={ICON_SIZE.sm} aria-hidden="true" />
        GA — pick at signup
      </span>
    );
  }
  if (status === 'design-partner-only') {
    return (
      <span className="legal-region-badge">
        <AlertTriangle size={ICON_SIZE.sm} aria-hidden="true" />
        Design-partner alpha
      </span>
    );
  }
  if (status === 'planned') {
    return (
      <span className="legal-region-badge">
        <Clock size={ICON_SIZE.sm} aria-hidden="true" />
        Planned — no ETA
      </span>
    );
  }
  return <span className="legal-meta">{status}</span>;
}
